import { createHash } from 'node:crypto';
import type { PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import express, { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, parse } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import { MAX_CSV_BYTES, MAX_CSV_ROWS, parseCsv, unneutraliseCell } from '../csv/csv.parser.js';
import { isoOf, toDate } from '../sales/documents.service.js';
import { parseDateCell } from '../sales/sales.import.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const D = (v: Decimal.Value | { toString(): string } | null | undefined) =>
  new Decimal((v ?? 0).toString());
const DAY = 86_400_000;
const norm = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_');

const ALIASES: Record<string, string[]> = {
  date: ['date', 'transaction_date', 'value_date', 'posting_date', 'txn_date'],
  description: [
    'description',
    'narration',
    'details',
    'particulars',
    'transaction_details',
    'memo',
  ],
  amount: ['amount', 'net_amount'],
  debit: ['debit', 'withdrawal', 'withdrawals', 'money_out', 'paid_out', 'dr'],
  credit: ['credit', 'deposit', 'deposits', 'money_in', 'paid_in', 'cr'],
  reference: ['reference', 'ref', 'transaction_id', 'txn_id', 'receipt_no', 'cheque_no'],
};

/**
 * Bank / mobile money statement reconciliation (PLAN.md phase 8). A statement CSV is loaded, every line is
 * matched to a ledger entry on the account (exact amount, nearby date), and what is left over is the list of
 * things to investigate. Matching never posts anything: differences are fixed with normal documents.
 */
export function createReconciliationRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/banking/reconciliation', authenticate({ db, config }));
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
  const need = (req: Request, p: string) => {
    if (!can(req, p))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };
  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  async function account(req: Request) {
    const a = await db.financialAccount.findFirst({
      where: { id: Number(req.params.accountId), companyId: req.auth!.companyId },
    });
    if (!a) throw new HttpError(404, 'NOT_FOUND', 'Account not found');
    return a;
  }
  const signed = (l: { debit: Decimal.Value; credit: Decimal.Value }) =>
    D(l.debit).minus(D(l.credit));

  router.post(
    '/banking/reconciliation/:accountId/import',
    express.text({ type: '*/*', limit: MAX_CSV_BYTES }),
    asyncHandler(async (req, res) => {
      need(req, 'banking.reconciliation.edit');
      const acct = await account(req);
      if (acct.currency !== 'UGX')
        throw new HttpError(422, 'UNSUPPORTED', 'Statement matching supports UGX accounts for now');
      const text = typeof req.body === 'string' ? req.body.replace(/^\uFEFF/, '') : '';
      const rows = parseCsv(text).filter((r) => r.some((c) => c.trim() !== ''));
      if (rows.length < 2) throw new HttpError(400, 'VALIDATION_ERROR', 'The file has no rows');
      if (rows.length - 1 > MAX_CSV_ROWS)
        throw new HttpError(413, 'TOO_MANY_ROWS', `At most ${MAX_CSV_ROWS} rows per file`);
      const header = rows[0]!.map(norm);
      const col = (key: string) => header.findIndex((h) => ALIASES[key]!.includes(h));
      const idx = {
        date: col('date'),
        description: col('description'),
        amount: col('amount'),
        debit: col('debit'),
        credit: col('credit'),
        reference: col('reference'),
      };
      if (idx.date < 0)
        throw new HttpError(400, 'VALIDATION_ERROR', 'Could not find a date column');
      if (idx.amount < 0 && idx.debit < 0 && idx.credit < 0)
        throw new HttpError(
          400,
          'VALIDATION_ERROR',
          'Could not find an amount (or debit and credit) column',
        );
      const num = (v: string | undefined) => {
        const t = (v ?? '').replace(/,/g, '').replace(/[()]/g, '').trim();
        return t === '' ? null : /^-?\d+(\.\d+)?$/.test(t) ? new Decimal(t) : NaN;
      };
      const errors: { row: number; message: string }[] = [];
      const parsed: {
        lineDate: Date;
        description: string;
        amount: Decimal;
        reference: string | null;
        fingerprint: string;
      }[] = [];
      const seen = new Map<string, number>();
      rows.slice(1).forEach((r, i) => {
        const rowNo = i + 2;
        const errs: string[] = [];
        const date = parseDateCell(r[idx.date], 'Date', errs);
        let amount: Decimal | null = null;
        if (idx.amount >= 0) {
          const a = num(r[idx.amount]);
          if (a === null || Number.isNaN(a)) errs.push('Amount is not a number');
          else amount = a as Decimal;
        } else {
          const dr = idx.debit >= 0 ? num(r[idx.debit]) : null;
          const cr = idx.credit >= 0 ? num(r[idx.credit]) : null;
          if ((dr !== null && Number.isNaN(dr)) || (cr !== null && Number.isNaN(cr)))
            errs.push('Debit/credit is not a number');
          else if (dr === null && cr === null) errs.push('Amount is missing');
          else
            amount = ((cr as Decimal | null) ?? new Decimal(0)).minus((dr as Decimal | null) ?? 0);
        }
        if (amount && amount.isZero()) errs.push('Amount is zero');
        if (errs.length || !date || !amount) {
          errors.push({ row: rowNo, message: errs.join('; ') || 'Invalid row' });
          return;
        }
        const description = unneutraliseCell((r[idx.description] ?? '').trim()).slice(0, 300);
        const reference =
          idx.reference >= 0
            ? unneutraliseCell((r[idx.reference] ?? '').trim()).slice(0, 100) || null
            : null;
        // Identical rows in one file are genuinely different transactions, so the fingerprint counts them.
        const base = `${date}|${amount.toFixed(4)}|${description}|${reference ?? ''}`;
        const n = (seen.get(base) ?? 0) + 1;
        seen.set(base, n);
        parsed.push({
          lineDate: toDate(date),
          description,
          amount,
          reference,
          fingerprint: createHash('sha256').update(`${base}|${n}`).digest('hex'),
        });
      });
      if (errors.length) {
        res.status(422).json({
          error: {
            code: 'INVALID_FILE',
            message: 'Nothing was imported. Fix these rows and upload again.',
            details: errors.slice(0, 50),
          },
        });
        return;
      }
      const existing = new Set(
        (
          await db.bankStatementLine.findMany({
            where: {
              financialAccountId: acct.id,
              fingerprint: { in: parsed.map((p) => p.fingerprint) },
            },
            select: { fingerprint: true },
          })
        ).map((x) => x.fingerprint),
      );
      const fresh = parsed.filter((p) => !existing.has(p.fingerprint));
      const batch = `imp-${Date.now().toString(36)}`;
      await db.$transaction(async (tx) => {
        for (const p of fresh)
          await tx.bankStatementLine.create({
            data: {
              companyId: acct.companyId,
              financialAccountId: acct.id,
              lineDate: p.lineDate,
              description: p.description,
              amount: p.amount.toString(),
              reference: p.reference,
              batch,
              fingerprint: p.fingerprint,
            },
          });
        await audit(tx, actor(req), {
          action: 'statement.imported',
          entity: 'financial_account',
          entityId: acct.id,
          after: { imported: fresh.length, duplicates: parsed.length - fresh.length, batch },
        });
      });
      res
        .status(201)
        .json({ imported: fresh.length, duplicates: parsed.length - fresh.length, batch });
    }),
  );

  /** Ledger lines on the account near the statement dates, minus those already matched. */
  async function candidates(
    acct: { id: number; ledgerAccountId: number; companyId: number },
    from: Date,
    to: Date,
  ) {
    const lines = await db.journalLine.findMany({
      where: {
        accountId: acct.ledgerAccountId,
        entry: {
          companyId: acct.companyId,
          status: 'posted',
          entryDate: {
            gte: new Date(from.getTime() - 10 * DAY),
            lte: new Date(to.getTime() + 10 * DAY),
          },
        },
      },
      include: { entry: true },
      orderBy: { id: 'asc' },
    });
    const taken = new Set(
      (
        await db.bankStatementLine.findMany({
          where: { financialAccountId: acct.id, journalLineId: { not: null } },
          select: { journalLineId: true },
        })
      ).map((x) => x.journalLineId),
    );
    return lines
      .filter((l) => !taken.has(l.id))
      .map((l) => ({
        id: l.id,
        date: isoOf(l.entry.entryDate)!,
        dateObj: l.entry.entryDate,
        amount: signed(l),
        description: l.memo ?? l.entry.description,
        entryNo: l.entry.entryNo,
        entryId: l.entry.id,
        reference: l.entry.reference,
      }));
  }

  router.get(
    '/banking/reconciliation/:accountId',
    asyncHandler(async (req, res) => {
      need(req, 'banking.reconciliation.view');
      const acct = await account(req);
      const status = typeof req.query.status === 'string' ? req.query.status : undefined;
      const all = await db.bankStatementLine.findMany({
        where: { financialAccountId: acct.id },
        orderBy: [{ lineDate: 'asc' }, { id: 'asc' }],
      });
      const lines = status ? all.filter((l) => l.status === status) : all;
      const from = all[0]?.lineDate ?? new Date();
      const to = all[all.length - 1]?.lineDate ?? new Date();
      const cands = all.length ? await candidates(acct, from, to) : [];
      const view = lines.map((l) => ({
        id: l.id,
        date: isoOf(l.lineDate),
        description: l.description,
        reference: l.reference,
        amount: l.amount.toFixed(4),
        status: l.status,
        journalLineId: l.journalLineId,
        suggestions:
          l.status === 'unmatched'
            ? cands
                .filter(
                  (c) =>
                    c.amount.equals(D(l.amount)) &&
                    Math.abs(c.dateObj.getTime() - l.lineDate.getTime()) <= 7 * DAY,
                )
                .sort(
                  (a, b) =>
                    Math.abs(a.dateObj.getTime() - l.lineDate.getTime()) -
                    Math.abs(b.dateObj.getTime() - l.lineDate.getTime()),
                )
                .slice(0, 3)
                .map((c) => ({
                  journalLineId: c.id,
                  date: c.date,
                  description: c.description,
                  entryNo: c.entryNo,
                  entryId: c.entryId,
                  amount: c.amount.toFixed(4),
                }))
            : [],
      }));
      // Ledger movements in the statement period that no statement line explains.
      const inRange = cands.filter((c) => all.length && c.dateObj >= from && c.dateObj <= to);
      const ledgerBal = await db.journalLine.aggregate({
        where: {
          accountId: acct.ledgerAccountId,
          entry: { companyId: acct.companyId, status: 'posted' },
        },
        _sum: { debit: true, credit: true },
      });
      const sum = (rows: typeof all, st: string) =>
        rows.filter((r) => r.status === st).reduce((s, r) => s.plus(D(r.amount)), new Decimal(0));
      res.json({
        account: {
          id: acct.id,
          name: acct.name,
          currency: acct.currency,
          ledgerBalance: D(ledgerBal._sum.debit).minus(D(ledgerBal._sum.credit)).toFixed(0),
        },
        period: all.length ? { from: isoOf(from), to: isoOf(to) } : null,
        summary: {
          lines: all.length,
          matched: all.filter((l) => l.status === 'matched').length,
          unmatched: all.filter((l) => l.status === 'unmatched').length,
          ignored: all.filter((l) => l.status === 'ignored').length,
          unmatchedTotal: sum(all, 'unmatched').toFixed(0),
          statementNet: all.reduce((s, r) => s.plus(D(r.amount)), new Decimal(0)).toFixed(0),
        },
        lines: view,
        unmatchedLedger: inRange.map((c) => ({
          journalLineId: c.id,
          date: c.date,
          description: c.description,
          entryNo: c.entryNo,
          entryId: c.entryId,
          amount: c.amount.toFixed(4),
        })),
      });
    }),
  );

  const matchSchema = z.object({
    statementLineId: z.number().int().positive(),
    journalLineId: z.number().int().positive(),
  });
  async function doMatch(req: Request, statementLineId: number, journalLineId: number) {
    const acct = await account(req);
    const line = await db.bankStatementLine.findFirst({
      where: { id: statementLineId, financialAccountId: acct.id },
    });
    if (!line) throw new HttpError(404, 'NOT_FOUND', 'Statement line not found');
    if (line.status !== 'unmatched')
      throw new HttpError(409, 'INVALID_STATE', 'This line is already matched or ignored');
    const jl = await db.journalLine.findFirst({
      where: {
        id: journalLineId,
        accountId: acct.ledgerAccountId,
        entry: { companyId: acct.companyId, status: 'posted' },
      },
    });
    if (!jl) throw new HttpError(404, 'NOT_FOUND', 'Ledger line not found on this account');
    if (!signed(jl).equals(D(line.amount)))
      throw new HttpError(
        409,
        'AMOUNT_MISMATCH',
        'The amounts differ. Fix the difference with a document first, then match.',
      );
    if (
      await db.bankStatementLine.findFirst({
        where: { financialAccountId: acct.id, journalLineId },
      })
    )
      throw new HttpError(
        409,
        'ALREADY_MATCHED',
        'That ledger line is matched to another statement line',
      );
    await db.bankStatementLine.update({
      where: { id: line.id },
      data: {
        status: 'matched',
        journalLineId,
        matchedBy: req.auth!.userId,
        matchedAt: new Date(),
      },
    });
    await audit(db, actor(req), {
      action: 'statement.matched',
      entity: 'bank_statement_line',
      entityId: line.id,
      after: { journalLineId },
    });
  }

  router.post(
    '/banking/reconciliation/:accountId/match',
    asyncHandler(async (req, res) => {
      need(req, 'banking.reconciliation.edit');
      const b = parse(matchSchema, req.body);
      await doMatch(req, b.statementLineId, b.journalLineId);
      res.json({ ok: true });
    }),
  );
  router.post(
    '/banking/reconciliation/:accountId/unmatch',
    asyncHandler(async (req, res) => {
      need(req, 'banking.reconciliation.edit');
      const b = parse(z.object({ statementLineId: z.number().int().positive() }), req.body);
      const acct = await account(req);
      const line = await db.bankStatementLine.findFirst({
        where: { id: b.statementLineId, financialAccountId: acct.id },
      });
      if (!line) throw new HttpError(404, 'NOT_FOUND', 'Statement line not found');
      await db.bankStatementLine.update({
        where: { id: line.id },
        data: { status: 'unmatched', journalLineId: null, matchedBy: null, matchedAt: null },
      });
      await audit(db, actor(req), {
        action: 'statement.unmatched',
        entity: 'bank_statement_line',
        entityId: line.id,
      });
      res.json({ ok: true });
    }),
  );
  router.post(
    '/banking/reconciliation/:accountId/ignore',
    asyncHandler(async (req, res) => {
      need(req, 'banking.reconciliation.edit');
      const b = parse(
        z.object({
          statementLineId: z.number().int().positive(),
          reason: z.string().trim().min(3).max(200),
        }),
        req.body,
      );
      const acct = await account(req);
      const line = await db.bankStatementLine.findFirst({
        where: { id: b.statementLineId, financialAccountId: acct.id, status: 'unmatched' },
      });
      if (!line) throw new HttpError(404, 'NOT_FOUND', 'Unmatched statement line not found');
      await db.bankStatementLine.update({ where: { id: line.id }, data: { status: 'ignored' } });
      await audit(db, actor(req), {
        action: 'statement.ignored',
        entity: 'bank_statement_line',
        entityId: line.id,
        after: { reason: b.reason },
      });
      res.json({ ok: true });
    }),
  );
  router.post(
    '/banking/reconciliation/:accountId/auto-match',
    asyncHandler(async (req, res) => {
      need(req, 'banking.reconciliation.edit');
      const acct = await account(req);
      const open = await db.bankStatementLine.findMany({
        where: { financialAccountId: acct.id, status: 'unmatched' },
        orderBy: { lineDate: 'asc' },
      });
      if (!open.length) return void res.json({ matched: 0 });
      const cands = await candidates(acct, open[0]!.lineDate, open[open.length - 1]!.lineDate);
      const used = new Set<number>();
      let matched = 0;
      for (const l of open) {
        const near = cands.filter(
          (c) =>
            !used.has(c.id) &&
            c.amount.equals(D(l.amount)) &&
            Math.abs(c.dateObj.getTime() - l.lineDate.getTime()) <= 3 * DAY,
        );
        // Only a single, unambiguous candidate is matched automatically; anything else is left for a person.
        const others = open.filter(
          (o) =>
            o.id !== l.id &&
            D(o.amount).equals(D(l.amount)) &&
            Math.abs(o.lineDate.getTime() - l.lineDate.getTime()) <= 3 * DAY,
        );
        if (near.length === 1 && others.length === 0) {
          await doMatch(req, l.id, near[0]!.id);
          used.add(near[0]!.id);
          matched += 1;
        }
      }
      res.json({ matched });
    }),
  );

  return router;
}
